import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  AdminChatMessage,
  AdminChatMetadata,
  getAdminChatMessages,
  getAdminChatConversation,
  subscribeToAdminChatMessages,
  subscribeToAdminChatMetadata,
} from '../services/adminChatService';

// Função para mostrar notificação web
function showWebNotification(title: string, body: string) {
  if (!('Notification' in window)) {
    return;
  }

  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'admin-chat',
      requireInteraction: false,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        showWebNotification(title, body);
      }
    });
  }
}

export function useAdminChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [metadata, setMetadata] = useState<AdminChatMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastMessageCountRef = useRef<number>(0);
  const isFirstLoadRef = useRef<boolean>(true);

  // Solicitar permissão para notificações ao montar
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Carrega metadados da conversa
  useEffect(() => {
    let isMounted = true;
    let unsubscribeMetadata: (() => void) | null = null;

    async function loadMetadata() {
      if (!user?.uid) {
        if (isMounted) {
          setMetadata(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Criar/buscar conversa
        const metadataData = await getAdminChatConversation(user.uid);
        if (isMounted) {
          setMetadata(metadataData);
        }

        // Configurar listener em tempo real para metadados
        unsubscribeMetadata = subscribeToAdminChatMetadata(user.uid, (data) => {
          if (isMounted) {
            setMetadata(data);
          }
        });
      } catch (err) {
        if (isMounted) {
          setError('Não foi possível carregar a conversa com admin.');
          setMetadata(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadMetadata();

    return () => {
      isMounted = false;
      if (unsubscribeMetadata) {
        unsubscribeMetadata();
      }
    };
  }, [user]);

  // Carrega mensagens com listener em tempo real
  useEffect(() => {
    let isMounted = true;
    let unsubscribeMessages: (() => void) | null = null;

    async function setupMessageListener() {
      if (!user?.uid) {
        if (isMounted) {
          setMessages([]);
          lastMessageCountRef.current = 0;
          isFirstLoadRef.current = true;
        }
        return;
      }

      try {
        // Carregar mensagens iniciais
        const initialMessages = await getAdminChatMessages(user.uid);
        if (isMounted) {
          setMessages(initialMessages);
          lastMessageCountRef.current = initialMessages.length;
          isFirstLoadRef.current = false;
        }

        // Configurar listener em tempo real
        unsubscribeMessages = subscribeToAdminChatMessages(user.uid, (newMessages) => {
          if (!isMounted) return;

          // Verificar se há novas mensagens (apenas após o carregamento inicial)
          if (!isFirstLoadRef.current && newMessages.length > lastMessageCountRef.current) {
            const latestMessage = newMessages[newMessages.length - 1];

            // Mostrar notificação apenas se a última mensagem for do admin
            if (latestMessage && latestMessage.author === 'admin') {
              showWebNotification(
                'Nova mensagem do administrador',
                latestMessage.text.substring(0, 50) + (latestMessage.text.length > 50 ? '...' : ''),
              );
            }
          }

          lastMessageCountRef.current = newMessages.length;
          isFirstLoadRef.current = false;

          if (isMounted) {
            setMessages(newMessages);
          }
        });
      } catch (err) {
        console.error('Erro ao configurar listener:', err);
        if (isMounted) {
          setMessages([]);
        }
      }
    }

    setupMessageListener();

    return () => {
      isMounted = false;
      if (unsubscribeMessages) {
        unsubscribeMessages();
      }
    };
  }, [user]);

  return { messages, metadata, loading, error };
}







